import SuperChart, { type DateChartSeries, type PlotLine } from "./SuperChart"
import { api } from "~/utils/api"
import { formatDate, isBefore, startOfDayUTC } from "~/utils/datetime"
import { addMonths, addWeeks, isAfter, isSameDay, subMonths } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import AreaGraphSilhouette from "../skeletonSilhouette/AreaGraphSilhouette"
import { useMemo } from "react"
import { ChartType } from "~/constants/graphs"
import { roundForecast } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { useChartSeriesConfig } from "~/constants/colors"

type Props = {
  variantIds: string[]
  startDate: Date
  endDate: Date
  forecastDate: Date
  valueMode?: "gross" | "net"
  /** Renders just the chart (no Card/header), for embedding in another container. */
  bare?: boolean
}

export default function PreviousForecastComparisonGraph({
  variantIds,
  forecastDate,
  startDate,
  endDate,
  valueMode = "gross",
  bare = false,
}: Props) {
  const chartConfig = useChartSeriesConfig()
  const salesDate = subMonths(startDate, 3)
  const netMode = valueMode === "net"

  const { data: salesAndForecast, isLoading: salesAndForecastIsLoading } =
    api.product.salesAndForecasts.useQuery(
      {
        variantIds,
        startDate: salesDate,
        endDate,
      },
      { enabled: variantIds.length > 0 },
    )

  const { data: historicForecasts, isLoading: historicIsLoading } =
    api.forecasts.getHistoricForecast.useQuery({
      date: forecastDate,
      variantIds,
    })

  const groupedHistoric = useMemo(() => {
    if (!historicForecasts) return []
    const grouped = historicForecasts.map((value, index) => ({
      date: addWeeks(forecastDate, index),
      value: index === 0 ? value : value - (historicForecasts?.[index - 1] ?? 0),
    }))

    return grouped
  }, [historicForecasts, startDate, endDate, forecastDate])

  const salesSeries: DateChartSeries = useMemo(
    () => ({
      name: netMode ? `Net ${formatName("sale", true, true)}` : formatName("sale", true, true),
      data:
        salesAndForecast?.sales
          ?.filter(
            (x) =>
              (isSameDay(x.date, salesDate) || isAfter(x.date, salesDate)) &&
              isBefore(x.date, endDate),
          )
          .map(({ date, value, returns }) => ({
            milliseconds: date.getTime(),
            value: netMode ? value - (returns ?? 0) : value,
          })) ?? [],
      type: "spline",
      colors: [chartConfig.sales.color],
      dashStyle: chartConfig.sales.dashStyle,
    }),
    [salesAndForecast, netMode, chartConfig],
  )

  // Historic forecasts have no channel dimension, so the net view scales them by the
  // blended factor implied by the current forecast (Σnet / Σgross). Approximate by design.
  const aggregateNetFactor = useMemo(() => {
    if (!netMode) return 1
    const forecasts = salesAndForecast?.forecasts ?? []
    const gross = forecasts.reduce((acc, f) => acc + f.value, 0)
    if (gross === 0) return 1
    const net = forecasts.reduce((acc, f) => acc + (f.net ?? f.value), 0)
    return net / gross
  }, [salesAndForecast, netMode])

  const previousForecastsSeries: DateChartSeries = useMemo(
    () => ({
      name: netMode ? "Previous Forecast (net)" : "Previous Forecast",
      data:
        groupedHistoric
          ?.filter((x) => isBefore(x.date, endDate))
          .map(({ date, value }) => ({
            milliseconds: startOfDayUTC(date).getTime(),
            value: roundForecast(value * aggregateNetFactor),
          })) ?? [],
      type: "spline",
      colors: [chartConfig.demand.color],
      dashStyle: chartConfig.demand.dashStyle,
    }),
    [groupedHistoric, netMode, aggregateNetFactor, chartConfig],
  )

  const currentForecastsSeries: DateChartSeries = useMemo(
    () => ({
      name: netMode ? "Current forecast (net)" : "Current forecast",
      data:
        salesAndForecast?.forecasts
          ?.filter((x) => isBefore(x.date, endDate))
          .map(({ date, value, net }) => ({
            milliseconds: startOfDayUTC(date).getTime(),
            value: roundForecast(netMode ? (net ?? value) : value),
          })) ?? [],
      type: "areaspline",
      colors: [chartConfig.demand.color],
      dashStyle: chartConfig.demand.dashStyle,
    }),
    [salesAndForecast, netMode, chartConfig],
  )

  if (salesAndForecastIsLoading || historicIsLoading) {
    return bare ? (
      <AreaGraphSilhouette />
    ) : (
      <Card>
        <AreaGraphSilhouette />
      </Card>
    )
  }

  const plotLines: PlotLine[] = [
    {
      text: "6 months",
      value: addMonths(startDate, 6).getTime(),
      lineColor: "rgba(0,0,0,0.1)",
      textColor: "#FFF",
      backgroundColor: "rgba(0,0,0,0.4)",
      showOnHover: false,
      offset: { x: 0 },
      dashStyle: "Dash",
    },
    {
      text: "3 months",
      value: addMonths(startDate, 3).getTime(),
      lineColor: "rgba(0,0,0,0.1)",
      textColor: "#FFF",
      backgroundColor: "rgba(0,0,0,0.4)",
      showOnHover: false,
      offset: { x: 0 },
      dashStyle: "Dash",
    },
    {
      text: "1 month",
      value: addMonths(startDate, 1).getTime(),
      lineColor: "rgba(0,0,0,0.1)",
      textColor: "#FFF",
      backgroundColor: "rgba(0,0,0,0.4)",
      showOnHover: false,
      offset: { x: 0 },
      dashStyle: "Dash",
    },
  ]

  const chart = (
    <SuperChart
      type={ChartType.TimeSeries}
      data={[salesSeries, currentForecastsSeries, previousForecastsSeries]}
      plotLines={{ x: plotLines }}
    />
  )

  if (bare) return chart

  return (
    <Card>
      <CardHeader>
        <CardTitle> {netMode ? "Net demand" : "Demand"} (next 52 weeks) </CardTitle>
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            <div>
              Current forecast from: <span className="font-semibold">{formatDate(startDate)}</span>
            </div>
            <div>
              Comparison forecast from:{" "}
              <span className="font-semibold">{formatDate(forecastDate)}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>{chart}</CardContent>
    </Card>
  )
}
